import React from "react";
import { graphql, Link, useStaticQuery } from "gatsby";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { GatsbySeo } from "gatsby-plugin-next-seo";
import { Helmet } from "react-helmet";
import Layout from "../components/layout";
import { Button } from "react-bootstrap";
import Hero from "../components/hero";
import PricingCard from "../components/pricing-card";
import PricingCardLarge from "../components/pricing-card-large";
import SingleFAQ from "../components/single-faq";
import Faqs from "../components/faqs";

const FrequesntlyAskedQuestionsPage = () => {
  const data = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
          }
        }
        heroImg: file(relativePath: { eq: "11.jpg" }) {
          publicURL
          childImageSharp {
            fluid(quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    `
  );
  const siteUrl = data.site.siteMetadata.siteUrl;
  const schemaImage = data.heroImg.publicURL;
  const title = "Frequently Asked Questions";
  const description =
    "FAQ: What happens in a business/executive coaching session? Jason has a structured but flexible agenda for meetings with his small business owner clients.";
  const heroImg = data.heroImg.childImageSharp.fluid;
  const ogImg = `${siteUrl}${schemaImage}`;

  const faqs = [
    {
      question:
        "How is Jason’s approach different from other business coaches, mentors, or consultants?",
      answer: (
        <>
          <p>
            Jason is a battle-hardened business owner who has owned and run
            businesses for nearly 40 years. He has never been employed. That means
            he understands exactly what it’s like to be the single decision-maker,
            with all the pressure, responsibility and rewards that come with it.
          </p>
          <br />
          <p>
            He combines that real-world experience with solid professional
            qualifications — a Diploma in Management Studies (DMS), a Master’s in
            Business Administration (MBA), and five years of training in
            counselling and psychotherapy. This mix of commercial know-how and
            psychological insight allows him to balance empathy, strategy, and
            accountability in every coaching relationship.
          </p>
          <br />
          <p>
            Clients describe his style as “down-to-earth, practical and supportive,
            with insightful questions and just the right amount of challenge.”
          </p>
          <br />
          <p>
            There’s no one-size-fits-all formula or motivational fluff. Everything
            you’ll do together is tailored to you — your goals, your mindset, and
            your business model — so you get guidance that’s grounded, relevant,
            and results-driven.
          </p>
          <br />
          <p>
            You can read or listen to the words of over 100 of Jason’s clients here:
          </p>
          <Link to="/written-testimonials">Written Testimonials</Link>
          <br />
          <Link to="/video-testimonials">Video Testimonials</Link>
        </>
      ),
    },
    {
      question: "What types of results do Jason’s clients typically achieve?",
      answer: (
        <>
          <p>
            Every client’s journey with Jason is different, but the common theme is
            transformation. Clients report feeling clearer, calmer, and more
            confident about their business and their future. They make better
            decisions, take consistent action, and stop wasting time and energy on
            what doesn’t matter.
          </p>
          <br />
          <p>
            Tangible results often include improved profitability, better systems
            and structure, a stronger mindset, and a more balanced life. Many
            clients achieve goals they once thought were out of reach — whether
            that’s growing their business, hiring a team, or rediscovering the joy
            of running it.
          </p>
          <br />
          <p>Based on 120 testimonials, five commonly mentioned outcomes are:</p>
          <ul>
            <li>Improved profitability and business growth</li>
            <li>Increased confidence, energy, and enthusiasm</li>
            <li>Better work-life balance and reduced stress</li>
            <li>Clarity of vision, goals, and strategy</li>
            <li>Transformation of mindset and personal growth</li>
          </ul>
          <br />
          <p>You can read or watch Jason’s clients here:</p>
          <Link to="/written-testimonials">Written Testimonials</Link>
          <br />
          <Link to="/video-testimonials">Video Testimonials</Link>
        </>
      ),
    },
    {
      question:
        "Who is coaching with Jason a great fit for and who shouldn’t work with him?",
      answer: (
        <>
          <p>
            Jason’s clients are small-business owners, freelancers, and
            professionals who are the single decision-makers in their business or
            practice. They want clarity, structure, confidence, and a trusted
            “hidden business partner” to challenge, support, and guide them.
          </p>
          <br />
          <p>Coaching with Jason may not be suitable if:</p>
          <ul>
            <li>You’re looking for quick fixes without accountability</li>
            <li>You prefer to be told what to do rather than explore solutions</li>
            <li>You’re unwilling or unable to commit time or energy between sessions</li>
          </ul>
          <br />
          <p>
            If you’re proactive, open-minded, and serious about positive change,
            you’ll work brilliantly together.
          </p>
        </>
      ),
    },
    {
      question: "How confidential is coaching with Jason?",
      answer: (
        <>
          <p>
            Completely confidential. What you share in your sessions with Jason
            stays between you. Sessions are not recorded. Jason provides a safe,
            non-judgemental space where you can speak openly about your business,
            finances, mindset, or personal challenges without worrying that your
            information will go anywhere else.
          </p>
          <br />
          <p>
            Confidentiality and trust are at the heart of Jason’s coaching
            relationships. It’s the foundation that allows real honesty and
            progress to take place.
          </p>
        </>
      ),
    },
    {
      question: "How long does coaching with Jason last?",
      answer: (
        <>
          <p>
            That depends on what you’d like to achieve. Some clients start with a
            short, focused programme such as Jason’s Business MOT / Health Check,
            designed to tackle specific challenges and create a clear way forward.
          </p>
          <br />
          <p>
            The most popular are the Momentum Builder (6 months) or Transformation
            Journey (12 months) for deeper and lasting results.
          </p>
          <br />
          <p>
            Many clients continue beyond their initial package, often renewing many
            times because they find the structure, accountability, and clarity
            invaluable.
          </p>
          <Link to="/packages">View Packages</Link>
        </>
      ),
    },
    {
      question: "What is Jason’s coaching style like in practice?",
      answer: (
        <>
          <p>
            Clients describe Jason’s approach as calm, empathetic, and
            down-to-earth, with plenty of practical advice and just the right amount
            of challenge. He listens carefully, asks insightful questions, and
            helps you see things from a different angle.
          </p>
          <br />
          <p>
            Jason is supportive when you need encouragement but holds you
            accountable for taking action between sessions. Each meeting is a
            focused, confidential conversation designed to move you forward —
            grounded and results-driven coaching.
          </p>
        </>
      ),
    },
    {
      question: "How does Jason measure progress and success during coaching?",
      answer: (
        <>
          <p>
            At the start of your journey, you’ll identify what success looks like
            for you — whether that’s increasing profitability, working fewer hours,
            improving confidence, or feeling more in control.
          </p>
          <br />
          <p>
            Together, you’ll review progress, celebrate wins, and refine goals as
            needed. Coaching isn’t about ticking boxes; it’s about measurable
            improvements in how you think, decide, and act every day.
          </p>
        </>
      ),
    },
    {
      question: "What can I expect from a discovery meeting with Jason?",
      answer: (
        <>
          <p>
            Jason looks forward to learning more about you and your business. With
            40 years of experience owning and running businesses, he understands how
            challenging decision-making can be.
          </p>
          <br />
          <p>
            The Discovery Meeting is a friendly, relaxed, and confidential 30-minute
            conversation designed to give you space to step back, gain perspective,
            and move forward with clarity.
          </p>
          <ul>
            <li>
              Open conversation about your business, goals, and challenges — no
              jargon, no pressure.
            </li>
            <li>A safe space to talk honestly about what’s working and what’s not.</li>
            <li>
              Insights and observations from decades of business and coaching
              experience.
            </li>
            <li>Clarity about priorities and next steps.</li>
            <li>A clear way forward if you both agree to work together.</li>
          </ul>
        </>
      ),
    },
    {
      question: "How do I prepare for a discovery meeting with Jason?",
      answer: (
        <>
          <p>
            You don’t need to bring anything formal, but taking a few minutes to
            think about these questions will make your meeting more productive:
          </p>
          <ul>
            <li>What’s going well in your business right now?</li>
            <li>What challenges or frustrations are you facing?</li>
            <li>What would success look like in 12 months?</li>
            <li>If nothing changes, how would that feel?</li>
            <li>
              What would you choose to work on first in your initial coaching
              session?
            </li>
            <li>
              What kind of progress would make you feel coaching is a worthwhile
              investment?
            </li>
          </ul>
          <br />
          <p>
            This isn’t an interview — just a meaningful, open conversation. Jason
            looks forward to your chat; it could be the start of something great.
          </p>
        </>
      ),
    },
    {
      question: "How do I get started?",
      answer: (
        <>
          <p>
            The first step is simple: book a free, no-obligation discovery meeting
            using the contact form on Jason’s website:
          </p>
          <Link to="/contact-us">Contact Jason</Link>
          <br />
          <br />
          <p>
            You’ll discuss where you are now, what you’d like to achieve, and
            whether working together feels like the right fit.
          </p>
          <p>
            If it does, Jason will suggest the most suitable coaching package and
            explain exactly what’s involved — no pressure, no hard sell, just an
            open conversation to help you move forward with clarity and confidence.
          </p>
        </>
      ),
    },
  ];
  

  const breadcrumb = {
    "@context": "http://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: {
          url: `${siteUrl}`,
          id: `${siteUrl}`,
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Packages and Prices",
        item: {
          url: `${siteUrl}/frequently-asked-questions`,
          id: `${siteUrl}/frequently-asked-questions`,
        },
      },
    ],
  };

  return (
    <Layout>
      <Helmet>
        {" "}
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      </Helmet>
      <GatsbySeo
        title={title}
        description={description}
        language="en"
        openGraph={{
          type: "website",
          url: `${siteUrl}/frequently-asked-questions`,
          title: `${title}`,
          description: `${description}`,
          images: [
            {
              url: `${ogImg}`,
              width: 1920,
              height: 1080,
              alt: "Frequently Asked Questions for Jason Cornes Business Coach",
            },
          ],
        }}
      />
      <Hero
        backgroundImage={heroImg}
        title="Frequently Asked Questions"
        isDarken={true}
      />

      <section
        className="py-lg-7 pb-5 pt-7"
        style={{ backgroundColor: "#F5F5F5" }}
      >
        <Container>
          <Row>
            {/* <SingleFAQ
              question="What happens in a typical business/executive coaching session with Jason?
"
              response={
                <>
                  <p className="d-inline">
                    As a potential client of Jason's, you might be wondering
                    what to expect from a typical business coaching session.
                    Jason has a structured but flexible agenda for meetings with
                    his small business owner clients.
                  </p>
                  <br />
                  <br />

                  <ol>
                    <li>Rapport-Building</li>
                    <li>Goal setting and Progress Tracking</li>
                    <li>Accountability and Motivation</li>
                    <li>Defining the Goal for the Session</li>
                    <li>Discussion and Problem-Solving</li>
                    <li>Action Points and Timeline</li>
                    <li>Feedback and Improvement</li>
                    <li>Book next Session!</li>
                  </ol>
                </>
              }
              videoLink="https://youtu.be/7DQfBdSC_Fo"
            /> */}
            <Faqs faqs={faqs} />
          </Row>
        </Container>
      </section>

      <section className="py-5 bg-secondary text-white">
        <Container>
          <Row>
            <Col>
              <h2>First Step - Discovery meeting</h2>
            </Col>
          </Row>
          <Row>
            <Col>
              <p>
                The first step is for us to have a discovery meeting. It will be
                great to meet you and listen about your circumstances, what
                you’d like to achieve and discuss how I can help. I won’t sell
                to you. Instead, I will give you feedback and suggest a way
                forward that I think would be most helpful to you. Please get in
                touch via the contact page, and we’ll get a day and time into
                our diaries.
              </p>
            </Col>
          </Row>
          <Row className="text-center mt-3">
            <Col className="d-flex align-items-center flex-column flex-md-row">
              <Button
                variant="outline-light"
                className="cta-btn cta-btn-gtm mr-md-4"
                size="lg"
                as={Link}
                to="/contact-us"
              >
                Book Your Discovery Meeting
              </Button>
              <Button
                as={Link}
                variant="primary"
                className="float-md-right w-fit cta-btn-gtm px-4 mt-4 mt-md-0"
                href={"https://quiz.jasoncornes.co.uk"}
                size="lg"
              >
                Quiz: Discover your business personality
              </Button>
            </Col>
          </Row>
        </Container>
      </section>
    </Layout>
  );
};
export default FrequesntlyAskedQuestionsPage;
